
export const STARTING_YEAR = 2018;

export const SECOND = 1000;
export const MINUTE = 60000;
export const HOUR = 3600000;
export const DAY = 86400000;
export const WEEK = 604800000;
export const MONTH = 2592000000;
export const YEAR = 31536000000;

export const UNIX_SECOND = SECOND / 1000;
export const UNIX_MINUTE = MINUTE / 1000;
export const UNIX_HOUR = HOUR / 1000;
export const UNIX_DAY = DAY / 1000;
export const UNIX_WEEK = WEEK / 1000;
export const UNIX_MONTH = MONTH / 1000;
export const UNIX_YEAR = YEAR / 1000;

export const NAME_SECOND = 'SECOND';
export const NAME_MINUTE = 'MINUTE';
export const NAME_HOUR = 'HOUR';
export const NAME_DAY = 'DAY';
export const NAME_WEEK = 'WEEK';
export const NAME_MONTH = 'MONTH';
export const NAME_YEAR = 'YEAR';

export const MONTH_NAMES = [
    'Styczeń',
    'Luty',
    'Marzec',
    'Kwiecień',
    'Maj',
    'Czerwiec',
    'Lipiec',
    'Sierpień',
    'Wrzesień',
    'Październik',
    'Listopad',
    'Grudzień',
];

/**
 * Formatuje ilość sekund do czasu w formacie HH:MM:SS
 *
 *  H - godziny z zerem wiodącym
 *  G - godziny bez zera wiodącego
 *  i - minuty godzinowe z zerem wiodącym
 *  I - minuty godzinowe bez zera wiodącego
 *  M - minuty całkowite
 *  s - sekundy z zerem wiodącym
 *  S - sekundy bez zera wiodącego
 *  \ - escape
 *  pozostałe znaki traktowane są jako separatory
 *
 * @param {number} seconds
 * @param {string} format
 * @returns {string}
 */
export const secondsToTime = (seconds, format = 'H:i:s') => {
    // Legacy wrapper
    if (_.isObject(format)) {
        format = `H:i${format.seconds !== false ? ':s' : ''}`;
    }

    let time = seconds < 0 ? '-' : '';
    seconds = Math.abs(Number(seconds));
    const h = Math.floor(seconds / UNIX_HOUR);
    const m = Math.floor(seconds / UNIX_MINUTE % UNIX_MINUTE);
    const s = Math.floor(seconds % UNIX_MINUTE);
    const totalMinutes = Math.floor(seconds / 60);
    const formatLength = format.length;
    let escape = false;

    for (let i = 0; i < formatLength; i++) {
        if (escape) {
            time += format[i];
            escape = false;
            continue;
        }
        switch (format[i]) {
            case 'H':
                time += _.padStart(h, 2, '0');
                break;

            case 'G':
                time += h;
                break;

            case 'i':
                time += _.padStart(m, 2, '0');
                break;

            case 'I':
                time += m;
                break;

            case 'M':
                time += totalMinutes;
                break;

            case 's':
                time += _.padStart(s, 2, '0');
                break;

            case 'S':
                time += s;
                break;

            case '\\':
                escape = true;
                break;

            default:
                time += format[i];
        }
    }
    return time;
};

/**
 * Parsuje czas w formacie HH:MM:SS do ilości sekund
 *
 * Format:
 *  h - godziny
 *  i - minuty
 *  s - sekundy
 *  pozostałe znaki traktowane są jako separatory i ignorowane
 *
 * @param {string} time
 * @param {string} format
 * @returns {number}
 */
export const timeToSeconds = (time, format = 'h:i:s') => {
    let seconds = 0;
    let formatLength = format.length;
    const weights = [];
    let regex = '(-*)';
    for (let i = 0; i < formatLength; i++) {
        switch (String(format[i]).toLowerCase()) {
            case 'h':
                regex += '(\\d+)';
                weights.push(UNIX_HOUR);
                break;

            case 'i':
                regex += '(\\d+)';
                weights.push(UNIX_MINUTE);
                break;

            case 's':
                regex += '(\\d+)';
                weights.push(UNIX_SECOND);
                break;

            default:
                regex += format[i];
        }
    }

    const matches = String(time).match(new RegExp(regex));
    if (matches === null) {
        throw `Błąd parsowania - '${time}' nie pasuje do wzorca '${format}`;
    }

    const isNegative = matches[1] === '-';
    for (let i = 2; i < matches.length; i++) {
        seconds += Number(matches[i]) * weights[i - 2];
    }

    if (isNegative) {
        seconds *= -1;
    }
    return seconds;
};


/**
 * Generuje listę godzin
 * @param {String} startHour Godzina początkowa w formacie HH:MM
 * @param {String} endHour Godzina końcowa w formacie HH:MM
 * @param {String} step Krok w formacie HH:MM
 * @returns {String[]}
 */
export const generateHoursList = (startHour = '08:00', endHour = '16:00', step = '00:30') => {
    step = timeToSeconds(step, 'h:i');
    startHour = timeToSeconds(startHour, 'h:i');
    endHour = timeToSeconds(endHour, 'h:i');
    const data = [];
    while (startHour <= endHour) {
        data.push(secondsToTime(startHour, 'H:i'));
        startHour += step;
    }
    return data;
};


/**
 * Zwraca ile czasu w sekundach upłynęło pomiędzy datami
 * @returns {number}
 * @param {Date} dateFrom
 * @param {Date} dateTo
 */
export const getElapsedTime = (dateFrom, dateTo = new Date()) => {
    if (!(dateFrom instanceof Date)) {
        dateFrom = new Date(dateFrom);
    }
    return Math.round((dateTo.valueOf() - dateFrom.valueOf()) / 1000);
};

/**
 * Zwraca czy string ma część z godziną
 * @param {string} dateString
 * @returns {boolean}
 */
export const hasHourPart = dateString => Boolean(String(dateString).match(/\d{2}:\d{2}/i));


/**
 * Zwraca część daty ISO (YYYY-MM-DD)
 * @param {string|Date} date
 * @returns {string}
 */
export const getISODatePart = date => {
    if (!date) {
        return '';
    }
    return moment(date).toISOString(true).slice(0, 10);
};

/**
 * Zwraca część daty ISO (YYYY-MM-DD)
 * @param {string|Date} date
 * @returns {string}
 */
export const getISODateTime = date => {
    if (!date) {
        return '';
    }
    return moment(date).toISOString(true);
};

/**
 * Formatuje datę
 * @param {string|Date} date
 * @param {string} format
 * @returns {string}
 */
export const formatDate = (date, format = 'LLL') => {
    if (!date || !format) {
        return '';
    }
    return moment(date).format(format);
};

/**
 * Zwraca ile dni ma dany miesiąc
 * @param {Date|string|null} date
 * @returns {number}
 */
export const daysInMonth = (date) => {
    return moment(date).daysInMonth();

}

/**
 * Zwraca czy data wypada w sobotę
 * @param {Date|string|null} date
 * @returns {boolean}
 */
export const isSaturday = (date) => {
    return moment(date).day() === 6;
};

/**
 * Zwraca czy data wypada w niedzielę
 * @param {Date|string|null} date
 * @returns {boolean}
 */
export const isSunday = (date) => {
    return moment(date).day() === 0;
};

/**
 * Zwraca czy data wypada w weekend
 * @param {Date|string|null} date
 * @returns {boolean}
 */
export const isWeekend = (date) => {
    return isSaturday(date) || isSunday(date);
};

/**
 * @param {Date} date
 * @returns {number}
 */
export const getWeekNumber = (date) => {
    return moment(date).daysInMonth();
}

/**
 * @param {Date|string} date
 * @param {string} interval
 * @returns {boolean}
 */
export const isToday = (date, interval) => {
    interval = String(interval).toLowerCase();
    return moment().isSame(date, interval);
};

/**
 *
 * @param {Date|string} dateA
 * @param {Date|string} dateB
 * @param {string|null} interval
 * @returns {boolean}
 */
export const isAfter = (dateA, dateB, interval = null) => {
    if (!dateA || !dateB) {
        return false;
    }
    return moment(dateA).isAfter(dateB, interval);
};

/**
 *
 * @param {Date|string} dateA
 * @param {Date|string} dateB
 * @param {string|null} interval
 * @returns {boolean}
 */
export const isSameOrAfter = (dateA, dateB, interval = null) => {
    if (!dateA || !dateB) {
        return false;
    }
    return moment(dateA).isSameOrAfter(dateB, interval);
};

/**
 *
 * @param {Date|string} dateA
 * @param {Date|string} dateB
 * @param {string|null} interval
 * @returns {boolean}
 */
export const isBefore = (dateA, dateB, interval = null) => {
    if (!dateA || !dateB) {
        return false;
    }
    return moment(dateA).isBefore(dateB, interval);
};

/**
 *
 * @param {Date|string} dateA
 * @param {Date|string} dateB
 * @param {string|null} interval
 * @returns {boolean}
 */
export const isSameOrBefore = (dateA, dateB, interval = null) => {
    if (!dateA || !dateB) {
        return false;
    }
    return moment(dateA).isSameOrBefore(dateB, interval);
};

/**
 *
 * @param {Date|string} dateA
 * @param {Date|string} dateB
 * @param {string|null} interval
 * @returns {boolean}
 */
export const isSame = (dateA, dateB, interval = null) => {
    if (!dateA || !dateB) {
        return false;
    }
    return moment(dateA).isSame(dateB, interval);
};

/**
 *
 * @param {Date|string} date
 * @param {Date|string} dateMin
 * @param {Date|string} dateMax
 * @param {String|null} interval
 * @return {boolean}
 */
export const isBetween = (date, dateMin, dateMax, interval = null) => {
    if (!date || !dateMin || !dateMax) {
        return false;
    }
    return moment(date).isBetween(dateMin, dateMax, interval)
};

export const setDatePart = (currentDate, nextDate) => {
    const date = moment(currentDate);
    const [year, month, day] = getISODatePart(nextDate).split('-');
    date.set({
        year: Number(year),
        month: Number(month) - 1,
        date: Number(day),
    });
    return date;
};

export const setTimePart = (date, time) => {
    const dateTime = moment(date);
    const [hour, minute, second] = String(time).split(':');
    dateTime.set({
        hour: Number(hour),
        minute: Number(minute),
        second: Number(second),
    });
    return dateTime;
};


export const startOf = (date, interval) => {
    return moment(date).startOf(interval).toDate();
};

export const endOf = (date, interval) => {
    return moment(date).endOf(interval).toDate();
};

export const addDate = (date, duration, interval) => {
    return moment(date).add(duration, interval).toDate();
};

export const subtractDate = (date, duration, interval) => {
    return moment(date).subtract(duration, interval).toDate();
};

export const asyncTimeout = (timeout) => {
    return new Promise((resolve) => {
        setTimeout(resolve, timeout);
    });
}
